// @flow
import React, { Component } from "react";
import shallowEqual from "fbjs/lib/shallowEqual";
import glamorous from "glamorous";
import cx from "classnames";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import tinycolor from "tinycolor2";
import type { UploadFileData } from "uploader";
import SvgIcon from "root/components/SvgIcon";
import { selectPalette } from "root/selectors/";
import getMediaQuery from "utils/sassVarsUtils";
import { getFileType } from "utils/fileUtils";
import { isMobile } from "utils/domUtils";
import queueConsts from "../../data/consts";
import PreviewImage from "../PreviewImage";
import { selectUploadQueueItems } from "../../selectors";
import { ASSETS_FORMATS } from "./consts";
import AssetStatus from "./AssetStatus";
import AssetAction from "./AssetAction";
import AssetInfo from "./AssetInfo";
import SVG_ICONS from "~/assets/icons/icons";

type OwnProps = {|
	className?: string,
	id?: string,
	gutter?: number,
	abort?: (string) => void,
	retryFailed?: (string) => void,
	paddingBottom: string,
	item?: UploadFileData,
|};

type Props = {
	...OwnProps,
	item: UploadFileData,
	palette: Object,
};

const { STATUSES } = queueConsts;

export const getItemStatus = (item: UploadFileData) => (item.aborted ? STATUSES.ABORTED
	: item.failed
		? STATUSES.ERROR
		: item.done ? STATUSES.DONE : STATUSES.UPLOADING);

const getTileBgColor = (colorFromPalette) => {
	const bg = tinycolor(colorFromPalette);
	const hsl = bg.toHsl();
	hsl.l = hsl.l > 0.5 ? hsl.l - 0.1 : hsl.l + 0.1;

	return tinycolor(hsl).toHslString();
};


const getPreviewImage = (item) => {
	const preview =  item.getPreviewObject?.(item) || {};

	if (item.uploadInfo?.preview_url) {
		preview.url = item.uploadInfo.preview_url;
	}

	return preview;
};

const getAssetIconColor = (colorFromPalette) => tinycolor(colorFromPalette).darken(25).toString();

const getFileIcon = (fileType: string) => ASSETS_FORMATS[fileType.toLowerCase()] || "FILE";


const NoPreview = ({ status, item, color }: { status: string, item: Object, color: string }) => {
	const fileType = getFileType(item);
	const fileIcon = status === STATUSES.ERROR || status === STATUSES.ABORTED ? "WARNING" : getFileIcon(fileType);

	return <SvgIcon dataTest="default-thumbnail-place-holder" size={70} color={color}
		path={SVG_ICONS[fileIcon]} className="absolute z-negative-1"/>;
};

const GBox = glamorous.div("aspect-ratio--object", (props) => {
	let styles = {
		backgroundColor: getTileBgColor(props.palette.sourceBg),
	};

	if (!isMobile()) {
		styles = {
			...styles,
			[`& .EXPANDED_ITEM--ASSET_ACTION,
            & .EXPANDED_ITEM--ASSET_INFO`
			]: {
				transition: "opacity 0.25s ease-in-out",
				opacity: 0,
			},
			[`&:HOVER .EXPANDED_ITEM--ASSET_ACTION,
            &:HOVER .EXPANDED_ITEM--ASSET_INFO`]: {
				opacity: 1,
			},
		};
	}

	return styles;
});

const GPreview = glamorous.div(cx(
	"EXPANDED_ITEM--PREVIEW",
	"flex items-center justify-center w-100 h-100",
), {
	"& img": {
		width: "100%",
		height: "auto",
		maxHeight: "100%",
		objectFit: "contain",
	},
});

const GExpandedItem = glamorous.div("EXPANDED-ITEM relative", ({ gutter }: { gutter: number }) => {
	if (gutter) {
		return {
			borderRightWidth: gutter,
			borderRightStyle: "solid",
			borderRightColor: "transparent",
		};
	}
});

const GAspectRatio = glamorous.div("EXPANDED-ITEM-ASPECT-RATIO aspect-ratio", ({ paddingBottom = "100%" }: { paddingBottom: string }) => ({
	paddingBottom: "100%",
	[getMediaQuery("ns")]: {
		paddingBottom,
	},
}));

const Title = ({ title }: { title: string }) => <glamorous.Div data-test="asset-name"
															   className="f6 fw5 pt3 truncate"
															   paddingBottom={17}
															   title={title}>{title}</glamorous.Div>;

export class ExpandedItem extends Component<Props> {
	componentDidMount() {
		const isIE11 = !!window.MSInputMethodContext && !!document.documentMode;

		if (isIE11) {
			const elem = this.element;

			setTimeout(() => {
				elem.style.top = "0";
			}, 100);
		}
	}

	element: HTMLElement;

	abortItem = () => {
		const { abort, item } = this.props;
		if (abort) {
			abort(item.id);
		}
	};

	retryItem = () => {
		const { retryFailed, item } = this.props;
		if (retryFailed) {
			retryFailed(item.id);
		}
	};

	render() {
		const {
			item, palette, className, gutter, paddingBottom,
		}: Props = this.props;
		const preview = getPreviewImage(item);
		const status = getItemStatus(item);
		const crop = item.customCoordinates;
		// dimensions can be different when resizing took place
		const dimensions = (crop && crop.width && item.dimensions && item.dimensions.length)
			? item.dimensions : item.imageDimensions;

		const renderNoPreview = () => <NoPreview status={status} item={item}
												 color={getAssetIconColor(palette.sourceBg)}/>;

		const previewImage = preview.url || preview.img
			? <PreviewImage
				img={preview.img}
				url={preview.url}
				noPreview={renderNoPreview}
				crop={crop}
				dimensions={dimensions}
			/> : renderNoPreview();

		return <GExpandedItem innerRef={(elem) => {
			this.element = elem;
		}} className={className} gutter={gutter} data-test="asset-container">
			<GAspectRatio paddingBottom={paddingBottom}>
				<GBox palette={palette}>
					<AssetStatus status={status} text={item.statusText} progress={item.progress}/>
					{item.id && (
						<>
							<AssetInfo item={item}/>
							<AssetAction status={status} onRetry={this.retryItem}
										 onAbort={this.abortItem} item={item}/>
						</>
					)}
					<GPreview>{previewImage}</GPreview>
				</GBox>
			</GAspectRatio>
			<Title title={item.name || ""}/>
		</GExpandedItem>;
	}
}

export const EQ_ATTRS = ["status", "progress", "done", "failed", "aborted", "paused"];

export const areMergedPropsEqual = (next: Props, prev: Props) => {
	const nextShallow = { ...next, item: null };
	const prevShallow = { ...prev, item: null };

	let isEq = shallowEqual(prevShallow, nextShallow);

	if (isEq) {
		const nItem = next.item;
		const pItem = prev.item;

		isEq = !EQ_ATTRS.find((attr) => nItem[attr] !== pItem[attr]);
	}

	return isEq;
};

export default connect<Props, OwnProps, _, _, _, _>(
	createStructuredSelector({
		palette: selectPalette,
		queueItems: selectUploadQueueItems,
	}),
	null,
	(stateProps: Object, dispatchProps: Object, ownProps: Object) => ({
		palette: stateProps.palette,
		item: stateProps.queueItems.find((i) => i.id === ownProps.id), // get the relevant queue item based on the id
		...dispatchProps,
		...ownProps,
	}),
	{
		areMergedPropsEqual, // enforce updates only for several of the item props
	},
)(ExpandedItem);
